import React from 'react';
import { Avatar, Box, Typography, CircularProgress } from '@mui/material';
import { PhotographerBadge } from './PhotographerBadge';
import { useResolvedUploaderProfile } from '../../hooks/useResolvedUploaderProfile';

/** Matches public-gallery Photo / album photo uploader shape */
export interface UploaderProfileLike {
   displayName?: string;
   name?: string;
   fullName?: string;
   profilePicture?: string;
   photoURL?: string;
   profilePhoto?: string;
   avatar?: string;
   accountType?: string[];
}

/** Album timeline / photo cards: label from profile only (same three fields). */
export function getUploaderDisplayName(p: UploaderProfileLike): string {
   return (
      p.displayName?.trim() ||
      p.name?.trim() ||
      p.fullName?.trim() ||
      ''
   );
}

/** Gate uploader UI: must have Firestore id on the media doc, same as photos. */
export function hasUploaderMediaIds(userId?: string, uploadedBy?: string): boolean {
   return Boolean(String(userId || uploadedBy || '').trim());
}

/** Uploader key for profile fetch: same for photos and videos (either field may be set on the media doc). */
export function getMediaUploaderUid(
   media?: { userId?: string; uploadedBy?: string } | null
): string {
   return String(media?.userId || media?.uploadedBy || '').trim();
}

export type AttributionColorScheme = 'onDarkMedia' | 'onLightMedia';

export interface MediaUploaderAttributionProps {
   /** From the media doc (photo or video); if only one of userId/uploadedBy is set, pass both. */
   userId?: string;
   uploadedBy?: string;
   /** From album API (e.g. public album initial fetch) — shows chip immediately without calling `/api/profile/public` */
   uploaderProfile?: UploaderProfileLike | Record<string, unknown> | null;
   onClick?: (e: React.MouseEvent) => void;
   /** Tile on dark media (gallery grid, wave) vs bar under lightbox */
   variant?: 'tileOverlay' | 'lightboxBar';
   avatarSize?: number;
   /** Extra sx for tile overlay positioning (e.g. bottom: 28 for WavePhoto above watermark) */
   overlaySx?: Record<string, unknown>;
   /** Use onLightMedia on grey/white preview backgrounds (e.g. album modal) */
   colorScheme?: AttributionColorScheme;
}

/**
 * Avatar + name + PhotographerBadge — same pattern as WavePhoto bottom overlay.
 */
export function MediaUploaderAttribution({
   userId,
   uploadedBy,
   uploaderProfile,
   onClick,
   variant = 'tileOverlay',
   avatarSize = 32,
   overlaySx,
   colorScheme = 'onDarkMedia',
}: MediaUploaderAttributionProps) {
   const uid = String(userId || uploadedBy || '').trim();
   const { profile: resolved, loading } = useResolvedUploaderProfile(uid || undefined, uploaderProfile ?? null);
   if (!uid) return null;
   const name = getUploaderDisplayName(resolved);
   const clickable = Boolean(onClick);
   const onLight = colorScheme === 'onLightMedia';

   if (loading && !name) {
      const spinner = (
         <CircularProgress
            size={Math.min(avatarSize, 28)}
            sx={{ color: onLight ? 'primary.main' : 'white' }}
         />
      );
      if (variant === 'lightboxBar') {
         return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1.5, px: 2 }}>
               {spinner}
            </Box>
         );
      }
      return (
         <Box
            sx={{
               position: 'absolute',
               bottom: 8,
               left: 8,
               zIndex: 2,
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               pointerEvents: 'none',
               ...overlaySx,
            }}
         >
            {spinner}
         </Box>
      );
   }

   if (!name) return null;

   const isPhotographer = (resolved.accountType || []).includes('photographer');

   const inner = (
      <>
         <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
               sx={{
                  width: avatarSize,
                  height: avatarSize,
                  border: onLight ? '2px solid' : '3px solid rgba(255,255,255,0.95)',
                  borderColor: onLight ? 'grey.400' : undefined,
                  boxShadow: onLight ? '0 1px 2px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.4)',
               }}
               src={
                  resolved.profilePicture ||
                  resolved.photoURL ||
                  resolved.profilePhoto ||
                  resolved.avatar
               }
            >
               {name.charAt(0) || '?'}
            </Avatar>
            {isPhotographer && <PhotographerBadge variant="avatarOverlay" size="sm" />}
         </Box>
         <Typography
            variant="caption"
            sx={{
               fontSize: variant === 'lightboxBar' ? '0.8125rem' : '0.75rem',
               fontWeight: 600,
               color:
                  variant === 'lightboxBar'
                     ? 'grey.300'
                     : onLight
                       ? 'text.primary'
                       : '#fff',
               px: 1,
               py: 0.5,
               borderRadius: 1,
               bgcolor:
                  variant === 'lightboxBar'
                     ? 'rgba(255,255,255,0.08)'
                     : onLight
                       ? 'rgba(255,255,255,0.92)'
                       : 'rgba(0,0,0,0.6)',
               display: 'inline-block',
               maxWidth: variant === 'lightboxBar' ? 280 : 140,
               overflow: 'hidden',
               textOverflow: 'ellipsis',
               whiteSpace: 'nowrap',
               textShadow: onLight ? 'none' : undefined,
            }}
            title={name}
         >
            {name}
         </Typography>
      </>
   );

   if (variant === 'lightboxBar') {
      return (
         <Box
            onClick={
               clickable && onClick
                  ? (e: React.MouseEvent) => {
                       e.stopPropagation();
                       onClick(e);
                    }
                  : undefined
            }
            sx={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               gap: 1,
               py: 1.5,
               px: 2,
               cursor: clickable ? 'pointer' : 'default',
               '&:hover': clickable ? { opacity: 0.9 } : {},
            }}
         >
            {inner}
         </Box>
      );
   }

   return (
      <Box
         onClick={(e) => {
            e.stopPropagation();
            if (clickable) onClick?.(e);
         }}
         sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            zIndex: 2,
            maxWidth: 'calc(100% - 56px)',
            color: onLight ? 'text.primary' : 'white',
            textShadow: onLight ? 'none' : '0 1px 2px rgba(0,0,0,0.8)',
            cursor: clickable ? 'pointer' : 'default',
            pointerEvents: 'auto',
            '&:hover': clickable ? { opacity: 0.9 } : {},
            ...overlaySx,
         }}
      >
         {inner}
      </Box>
   );
}
